/**
 * BookRideTab · v2.6.9 batch R
 *
 * Dispatcher / supervisor / admin booking console nested under
 * Driver Mgmt → "Book Ride". Mirrors the Custom Trip Builder UX from
 * the driver app, plus:
 *   · Rider info capture (first / last / phone / email / address)
 *   · Driver assignment dropdown sourced from /admin/drivers (active only)
 *   · Card OR cash payment toggle (Square Web Payments SDK for card)
 *   · Internal-only event log per trip (notes hidden from drivers + riders)
 *   · Driver bottleneck ping (urgent broadcast to all active drivers OR
 *     specifically-targeted drivers)
 *
 * Trip IDs are server-generated `BTS-DISP-XXXXX` so they're visually
 * distinct from rider-app waybills (BTW-…) and incident reports.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RiderFlagBanner } from "@/components/RiderFlagBanner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, Calculator, Route as RouteIcon, CreditCard, Banknote, Send,
  AlertTriangle, Plus, FileText, Printer, Download, Trash2, Users, MapPin, Search, RefreshCw,
  Check, X, ShieldCheck, Radio, Radar, ParkingSquare, Settings2, Pencil, Copy, CalendarClock, Gift, KeyRound, BadgeCheck,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import TierBadge from "@/components/shared/TierBadge";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import SquareCardForm from "@/components/SquareCardForm";
import PrepaidAdminPanel from "@/components/admin/PrepaidAdminPanel";
// v2.7.65+E158 · Dispatch Booking · shared Quick Booking form (customer-facing)
// is reused inside the admin console so dispatch agents get the exact same
// UX + validation + voucher path as public riders. The public homepage form
// remains untouched — this is an additive mount only.
import MarketingBookForm from "@/components/marketing/MarketingBookForm";
// Update 3 · Isolated TAXI Calculator — additive sibling rendered OUTSIDE the
// booking form. Own state/API; never reads or writes the booking form.
import TaxiCalculator from "@/components/admin/TaxiCalculator";

const TRIP_STATUSES = ["Pending Dispatch", "Driver Assigned", "Driver En-Route", "Picked Up", "Completed", "Cancelled"];

export default function BookRideTab() {
  const [tab, setTab] = useState("new");
  return (
    <>
    <Tabs value={tab} onValueChange={setTab} data-testid="book-ride-tab">
      <PrepaidAdminPanel/>
      <TabsList className="bg-zinc-900 border border-zinc-800 grid grid-cols-3 mb-4 h-auto p-1 gap-1">
        <TabsTrigger value="new" data-testid="bookride-tab-new" className="text-xs sm:text-sm py-2"><Plus className="h-3.5 w-3.5 mr-1"/>Dispatch Booking</TabsTrigger>
        <TabsTrigger value="list" data-testid="bookride-tab-list" className="text-xs sm:text-sm py-2"><Users className="h-3.5 w-3.5 mr-1"/>Bookings</TabsTrigger>
        <TabsTrigger value="ping" data-testid="bookride-tab-ping" className="text-red-300 data-[state=active]:text-red-200 text-xs sm:text-sm py-2"><AlertTriangle className="h-3.5 w-3.5 mr-1"/>Bottleneck Ping</TabsTrigger>
      </TabsList>
      <TabsContent value="new">
        {/* Go-Live · TAXI Calculator positioned ABOVE the booking form
            (still an isolated sibling, OUTSIDE the form). */}
        <TaxiCalculator/>
        {/* v2.7.65+E158 · Dispatch Booking form — same shared component the
            public homepage uses (MarketingBookForm), rendered in
            `dispatcherMode` so submissions are tagged `source=admin_dispatch`
            and the row lands in the Ride Coordinator + Live Queue with a
            distinctive "DISPATCH BOOKING" badge. Everything else — quote,
            voucher validation, stops, gender pref, scheduling, cash / card
            hold flows — behaves exactly like the customer experience. */}
        <div data-testid="dispatch-booking-wrapper" className="rounded-lg border-2 border-yellow-400/40 bg-gradient-to-br from-yellow-950/20 via-zinc-950 to-zinc-950 p-1">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded bg-yellow-400/20 text-yellow-200 border border-yellow-400/60" data-testid="dispatch-booking-badge">
                Dispatch Booking
              </span>
              <span className="text-[10px] text-zinc-500">
                Same rider experience as the public site — tagged internally for the Ride Coordinator.
              </span>
            </div>
          </div>
          <MarketingBookForm dispatcherMode={true}/>
        </div>
      </TabsContent>
      <TabsContent value="list"><BookingsList/></TabsContent>
      <TabsContent value="ping"><BottleneckPingPanel/></TabsContent>
    </Tabs>
    </>
  );
}

// ===================== New Booking =====================
function NewBookingForm({ onCreated }) {
  const [drivers, setDrivers] = useState([]);       // active online drivers only
  const [pingingId, setPingingId] = useState(null); // driver id currently being pinged
  const [presets, setPresets] = useState({ presets: [], airport_options: [], default_toll: 12, can_edit: false });
  const [selectedPresetId, setSelectedPresetId] = useState("");   // "weatherford" | "statewide" | ""
  const [airportOptionId, setAirportOptionId] = useState("");     // "none" | "standard" | "okc_flat" | …
  const [tollEnabled, setTollEnabled] = useState(false);
  const [showPresetEditor, setShowPresetEditor] = useState(false);
  const [rider, setRider] = useState({ first_name: "", last_name: "", phone: "", email: "", address: "" });
  // v2.6.9 batch Y · rider flag lookup (debounced on phone/email change)
  const [riderFlag, setRiderFlag] = useState(null);
  useEffect(() => {
    if (!rider.phone.trim() && !rider.email.trim()) { setRiderFlag(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await adminApi.get("/admin/customers/flag-lookup", {
          params: { phone: rider.phone.trim() || undefined, email: rider.email.trim() || undefined },
        });
        setRiderFlag(data?.flagged ? data : null);
      } catch { setRiderFlag(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [rider.phone, rider.email]);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [routing, setRouting] = useState(false);
  const [fare, setFare] = useState({ base_fare: 0, per_passenger: 0, passengers: 1, per_mile: 0, miles: 0, per_minute: 0, minutes: 0, stops: 0, per_stop_per_minute: 1, stop_minutes: 0, airport_fee: 0, toll_fee: 0 });
  const [calc, setCalc] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash"); // "card" | "cash" | "cashapp" | "venmo"
  const [assignedDriverId, setAssignedDriverId] = useState("");
  const [initialNote, setInitialNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  // v2.7.0 batch DC · Task 5 · advance-booking schedule toggle (matches public form)
  const [scheduleMode, setScheduleMode] = useState("asap");   // "asap" | "scheduled"
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");

  // v2.7.65+E180 · Source ACTIVE drivers (flag=true) instead of the
  // narrower "online right now" set so dispatchers can preassign a
  // ride to a driver who's on-shift but between pings. Endpoint returns
  // every `active: true` row with a fresh-heartbeat hint.
  const reloadOnlineDrivers = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/queued-trips/active-drivers");
      // Preserve the shape BookRideTab expects — the active-drivers
      // endpoint returns `drivers: [{id, name, phone, online, on_trip}]`.
      setDrivers(r.data?.drivers || []);
    } catch (e) {
      console.warn("active-drivers fetch failed", e?.response?.status);
    }
  }, []);
  useEffect(() => { reloadOnlineDrivers(); }, [reloadOnlineDrivers]);

  // v2.7.65+E111 · Fare-preset menu (Weatherford / Statewide + airport + toll)
  const reloadPresets = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/booked-trips/fare-presets");
      if (!r.data) return;
      setPresets(r.data);
      const dflt = (r.data.airport_options || []).find((o) => o.default);
      if (dflt) setAirportOptionId((prev) => prev || dflt.id);
    } catch (e) { console.warn("fare-presets fetch failed", e?.response?.status); }
  }, []);
  useEffect(() => { reloadPresets(); }, [reloadPresets]);

  // Apply a fare preset — populates base + per_mile + per_minute + per_passenger.
  const applyPreset = (id) => {
    setSelectedPresetId(id);
    const p = (presets.presets || []).find((x) => x.id === id);
    if (!p) return;
    setFare((f) => ({
      ...f,
      base_fare:            p.base_fare        ?? f.base_fare,
      per_mile:             p.per_mile         ?? f.per_mile,
      per_minute:           p.per_minute       ?? f.per_minute,
      per_passenger:        p.per_passenger    ?? f.per_passenger,
      per_stop_per_minute:  p.per_stop_per_minute ?? f.per_stop_per_minute,
    }));
    toast.success(`Loaded ${p.label} · $${p.base_fare} drop + $${p.per_mile}/mi`);
  };

  const applyAirport = (id) => {
    setAirportOptionId(id);
    const opt = (presets.airport_options || []).find((x) => x.id === id);
    if (!opt) return;
    setFare((f) => ({ ...f, airport_fee: Number(opt.amount) || 0 }));
  };

  const toggleToll = (enabled) => {
    setTollEnabled(enabled);
    setFare((f) => ({ ...f, toll_fee: enabled ? Number(presets.default_toll || 12) : 0 }));
  };

  const setFareNum = (k) => (e) => setFare((f) => ({ ...f, [k]: parseFloat(e.target.value) || 0 }));

  const fetchRoute = async () => {
    if (!origin || !destination) return toast.error("Enter both addresses");
    setRouting(true);
    try {
      const { data } = await adminApi.post("/maps/distance", { origin, destination });
      setFare((f) => ({ ...f, miles: data.distance_miles, minutes: data.duration_minutes }));
      toast.success(`Route: ${data.distance_miles} mi · ${data.duration_minutes} min`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Maps error"); }
    finally { setRouting(false); }
  };

  const calculate = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/fares/custom", { ...fare, cc_surcharge: paymentMethod === "card" });
      setCalc(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Calc error"); }
    finally { setBusy(false); }
  };

  // v2.7.65+E111 · Live driver ping — hits the canonical /admin/drivers/{id}/ping
  // endpoint so the driver's app gets a WS / push / SMS fanout, same as the
  // main Ride Coordinator dashboard. No new middleware — reuses the audit
  // trail in driver_ping_requests.
  const pingDriver = async (driverId) => {
    if (!driverId) return toast.error("Pick a driver to ping first");
    setPingingId(driverId);
    try {
      const r = await adminApi.post(`/admin/drivers/${driverId}/ping`);
      const channels = (r.data?.channels || []);
      if (r.data?.delivered) {
        toast.success(`Ping delivered · ${channels.length ? channels.join(" + ") : "socket"}`);
      } else {
        toast.info("Driver offline · ping queued");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ping failed");
    } finally { setPingingId(null); }
  };

  // Reusable submit fn — branches on whether we have a card token already.
  const submit = useCallback(async (cardData) => {
    if (!rider.first_name.trim()) return toast.error("Rider first name is required");
    if (!origin || !destination) return toast.error("Both addresses are required");
    if (!calc) return toast.error("Please calculate the fare first");
    setBusy(true);
    try {
      // v2.7.0 batch DC · Task 5 · validate + serialize the schedule fields
      let scheduledForIso = null;
      if (scheduleMode === "scheduled") {
        if (!scheduledDate || !scheduledTime) {
          toast.error("Pick a date AND time or switch to ASAP");
          setBusy(false); return;
        }
        const dt = new Date(`${scheduledDate}T${scheduledTime}`);
        if (Number.isNaN(dt.getTime())) {
          toast.error("Schedule date/time looks invalid"); setBusy(false); return;
        }
        scheduledForIso = dt.toISOString();
      }
      const payload = {
        rider,
        starting_address: origin,
        ending_address: destination,
        miles: fare.miles, minutes: fare.minutes,
        fare,
        subtotal: calc.subtotal,
        cc_surcharge: calc.cc_surcharge,
        total: calc.total,
        payment_method: paymentMethod,
        square_payment_id: cardData?.payment_id,
        card_snapshot: cardData ? { brand: cardData.brand, last4: cardData.last4 } : null,
        assigned_driver_id: assignedDriverId || null,
        initial_note: initialNote.trim() || null,
        // v2.7.0 batch DC · Task 5 · advance-booking metadata
        schedule_mode: scheduleMode,
        scheduled_for: scheduledForIso,
        pickup_at: scheduledForIso,
        scheduled: scheduleMode === "scheduled",
      };
      const { data } = await adminApi.post("/admin/booked-trips", payload);
      toast.success(`Booked · ${data.trip.id}`);
      // Reset form
      setRider({ first_name: "", last_name: "", phone: "", email: "", address: "" });
      setOrigin(""); setDestination(""); setCalc(null); setInitialNote("");
      setAssignedDriverId(""); setShowCardModal(false);
      setScheduleMode("asap"); setScheduledDate(""); setScheduledTime("");
      if (typeof onCreated === "function") onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); }
    finally { setBusy(false); }
  }, [rider, origin, destination, calc, fare, paymentMethod, assignedDriverId, initialNote, scheduleMode, scheduledDate, scheduledTime, onCreated]);

  const onClickBook = () => {
    if (paymentMethod === "card") setShowCardModal(true);
    else submit(null);
  };

  return (
    <div className="space-y-4" data-testid="bookride-new">
      <Card className="bg-zinc-900 border-amber-400/30 p-5 space-y-5">
        <header className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-amber-400">
            <Calculator className="h-5 w-5"/>
          </div>
          <div>
            <h2 className="text-lg font-bold">Dispatcher Booking</h2>
            <p className="text-[11px] text-zinc-500">Trip IDs are auto-generated as BTS-DISP-XXXXX. All notes added below stay internal.</p>
          </div>
        </header>

        <Section title="Rider Information">
          {riderFlag && (
            <RiderFlagBanner flag={riderFlag} data-testid="bookride-rider-flag"/>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field testid="rider-first-name" label="First Name *" value={rider.first_name} onChange={(e) => setRider({ ...rider, first_name: e.target.value })}/>
            <Field testid="rider-last-name" label="Last Name" value={rider.last_name} onChange={(e) => setRider({ ...rider, last_name: e.target.value })}/>
            <Field testid="rider-phone" label="Phone" value={rider.phone} onChange={(e) => setRider({ ...rider, phone: e.target.value })} placeholder="(580) 555-1234"/>
            <Field testid="rider-email" label="Email" value={rider.email} onChange={(e) => setRider({ ...rider, email: e.target.value })}/>
            <div className="sm:col-span-2"><Field testid="rider-address" label="Home Address" value={rider.address} onChange={(e) => setRider({ ...rider, address: e.target.value })}/></div>
          </div>
        </Section>

        <Section title="Trip Route">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field testid="bookride-origin" label="Starting Address *" value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Pickup address"/>
            <Field testid="bookride-destination" label="Ending Address *" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Drop-off address"/>
          </div>
          <Button onClick={fetchRoute} disabled={routing} variant="outline" className="bg-zinc-950 border-zinc-700 mt-2" data-testid="bookride-route-btn">
            {routing ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <RouteIcon className="h-4 w-4 mr-1"/>}Get Route Estimate
          </Button>
        </Section>

        {/* v2.7.0 batch DC · Task 5 · advance-booking schedule toggle (mirrors public form) */}
        <Section title="Schedule">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Would you like to book this trip in advance?</Label>
              <select
                value={scheduleMode}
                onChange={(e) => setScheduleMode(e.target.value)}
                data-testid="bookride-schedule-mode"
                className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 mt-1"
              >
                <option value="asap">No — ASAP</option>
                <option value="scheduled">Yes — schedule a date &amp; time</option>
              </select>
            </div>
            {scheduleMode === "scheduled" && (
              <>
                <Field testid="bookride-schedule-date" label="Pickup date" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} min={new Date().toISOString().slice(0, 10)}/>
                <Field testid="bookride-schedule-time" label="Pickup time" type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)}/>
              </>
            )}
          </div>
        </Section>

        <Section title="Fare components">
          {/* v2.7.65+E111 · Rate profile picker (Weatherford / Statewide) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Rate profile *</Label>
                {presets.can_edit && (
                  <button
                    type="button"
                    onClick={() => setShowPresetEditor(true)}
                    data-testid="fare-preset-editor-open"
                    className="text-[10px] uppercase tracking-widest font-bold text-amber-300 hover:text-amber-200 flex items-center gap-1"
                    title="Add or edit rate profiles (L5 only)"
                  >
                    <Settings2 className="h-3 w-3"/>Edit
                  </button>
                )}
              </div>
              <select
                value={selectedPresetId}
                onChange={(e) => applyPreset(e.target.value)}
                data-testid="fare-rate-profile"
                className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 mt-1"
              >
                <option value="">Custom · manual entry</option>
                {(presets.presets || []).map((p) => (
                  <option key={p.id} value={p.id} data-testid={`fare-preset-opt-${p.id}`}>
                    {p.label}
                  </option>
                ))}
              </select>
              {selectedPresetId && (
                <div className="text-[10px] text-amber-300 mt-1" data-testid="fare-preset-hint">
                  {(presets.presets || []).find((p) => p.id === selectedPresetId)?.description}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Airport pickup fee</Label>
              <select
                value={airportOptionId}
                onChange={(e) => applyAirport(e.target.value)}
                data-testid="fare-airport-option"
                className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100 mt-1"
              >
                {(presets.airport_options || []).map((opt) => (
                  <option key={opt.id} value={opt.id} data-testid={`fare-airport-opt-${opt.id}`}>
                    {opt.label} · ${(opt.amount || 0).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Toll fee</Label>
              <div className="mt-1 flex items-center gap-2 bg-zinc-950 border border-zinc-700 rounded px-3 py-2">
                <input
                  type="checkbox"
                  id="fare-toll-toggle"
                  checked={tollEnabled}
                  onChange={(e) => toggleToll(e.target.checked)}
                  data-testid="fare-toll-toggle"
                  className="h-4 w-4"
                />
                <label htmlFor="fare-toll-toggle" className="text-sm text-zinc-200 flex-1 cursor-pointer">
                  Add toll · ${Number(presets.default_toll || 12).toFixed(2)}
                </label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field testid="fare-base" label="Base Fare $" type="number" value={fare.base_fare} onChange={setFareNum("base_fare")}/>
            <Field testid="fare-per-passenger" label="Per Passenger $" type="number" value={fare.per_passenger} onChange={setFareNum("per_passenger")}/>
            <Field testid="fare-passengers" label="Passengers" type="number" value={fare.passengers} onChange={setFareNum("passengers")}/>
            <Field testid="fare-per-mile" label="Per Mile $" type="number" value={fare.per_mile} onChange={setFareNum("per_mile")}/>
            <Field testid="fare-miles" label="Miles" type="number" value={fare.miles} onChange={setFareNum("miles")}/>
            <Field testid="fare-per-min" label="Per Min $" type="number" value={fare.per_minute} onChange={setFareNum("per_minute")}/>
            <Field testid="fare-min" label="Minutes" type="number" value={fare.minutes} onChange={setFareNum("minutes")}/>
            <Field testid="fare-stops" label="Stops" type="number" value={fare.stops} onChange={setFareNum("stops")}/>
            <Field testid="fare-airport" label="Airport Fee $" type="number" value={fare.airport_fee} onChange={setFareNum("airport_fee")}/>
            <Field testid="fare-toll" label="Toll Fee $" type="number" value={fare.toll_fee} onChange={setFareNum("toll_fee")}/>
          </div>

          {/* v2.7.65+E111 · Live component breakdown so dispatch sees each
              line before hitting Calculate. Numbers echo current form state. */}
          <Card className="bg-zinc-950 border-zinc-800 p-3 mt-3" data-testid="fare-components-breakdown">
            <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">Live component breakdown</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-zinc-300">
              <BreakdownRow label="Base rate"       amount={fare.base_fare}                                    testid="brk-base"/>
              <BreakdownRow label="Passenger total" amount={(Number(fare.per_passenger) || 0) * (Number(fare.passengers) || 0)} testid="brk-passenger"/>
              <BreakdownRow label="Airport fee"     amount={fare.airport_fee}                                  testid="brk-airport"/>
              <BreakdownRow label="Toll fee"        amount={fare.toll_fee}                                     testid="brk-toll"/>
              <BreakdownRow label="Stops"           amount={(Number(fare.stops) || 0) * (Number(fare.per_stop_per_minute) || 0) * (Number(fare.stop_minutes) || 1)} testid="brk-stops" note={`${fare.stops || 0} stop${(fare.stops || 0) === 1 ? "" : "s"}`}/>
              <BreakdownRow label="Miles"           amount={(Number(fare.per_mile) || 0) * (Number(fare.miles) || 0)}    testid="brk-miles" note={`${fare.miles || 0} mi × $${fare.per_mile || 0}`}/>
            </div>
          </Card>

          <Button onClick={calculate} disabled={busy} className="mt-3 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="bookride-calc-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Calculator className="h-4 w-4 mr-1"/>}Calculate Fare
          </Button>
          {calc && (
            <Card className="bg-zinc-950 border-zinc-800 p-3 mt-3" data-testid="bookride-fare-preview">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1">Fare preview</div>
              <div className="text-xs text-zinc-300">Subtotal: ${calc.subtotal.toFixed(2)} {paymentMethod === "card" ? `· CC fee $${calc.cc_surcharge.toFixed(2)}` : ""} · <span className="font-bold text-amber-300">Total ${calc.total.toFixed(2)}</span></div>
            </Card>
          )}
        </Section>

        <Section title="Assign Active Driver (optional)">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <Select value={assignedDriverId} onValueChange={setAssignedDriverId}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700" data-testid="bookride-driver-select">
                  <SelectValue placeholder="Leave unassigned · Park in Ride Coordinator queue"/>
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-72">
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`bookride-drv-opt-${d.id}`}>
                      <span className="flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${d.online ? "bg-emerald-500" : "bg-zinc-500"}`}/>
                        {d.name}
                        {d.on_trip ? " · on trip" : ""}
                        {!d.online ? " · offline" : ""}
                      </span>
                    </SelectItem>
                  ))}
                  {drivers.length === 0 && (
                    <div className="px-3 py-2 text-xs text-zinc-500 italic">No active drivers configured</div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={reloadOnlineDrivers}
              className="bg-zinc-950 border-zinc-700 h-10"
              data-testid="bookride-driver-refresh"
              title="Refresh active driver list"
            >
              <RefreshCw className="h-3.5 w-3.5"/>
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1" data-testid="bookride-driver-hint">
            Shows every driver marked <span className="text-emerald-300 font-semibold">active</span> in the system ·{" "}
            {assignedDriverId
              ? "Driver will be assigned to this ride immediately on Book."
              : (<span className="inline-flex items-center gap-1 text-amber-300"><ParkingSquare className="h-3 w-3"/>Booking will park in the Ride Coordinator queue as unassigned.</span>)}
          </p>
        </Section>

        <Section title="Payment">
          <div className="flex items-center gap-3 flex-wrap">
            <PaymentPill kind="cash" current={paymentMethod} onClick={() => setPaymentMethod("cash")} icon={Banknote} label="Cash" testid="payment-cash"/>
            <PaymentPill kind="card" current={paymentMethod} onClick={() => setPaymentMethod("card")} icon={CreditCard} label="Card" testid="payment-card"/>
            <PaymentPill kind="cashapp" current={paymentMethod} onClick={() => setPaymentMethod("cashapp")} icon={Banknote} label="Cash App" testid="payment-cashapp"/>
            <PaymentPill kind="venmo" current={paymentMethod} onClick={() => setPaymentMethod("venmo")} icon={Banknote} label="Venmo" testid="payment-venmo"/>
          </div>
          {paymentMethod === "card" && <p className="text-[10px] text-zinc-500 mt-2">Card capture happens on Book — Square Web Payments SDK opens to tokenize the card.</p>}
        </Section>

        <Section title="Internal note (optional)">
          <Textarea
            data-testid="bookride-initial-note"
            value={initialNote}
            onChange={(e) => setInitialNote(e.target.value)}
            placeholder="Special instructions / context dispatch should remember (rider needs wheelchair help, will pay cash, etc)"
            className="bg-zinc-950 border-zinc-700 min-h-[80px]"
          />
          <p className="text-[10px] text-zinc-500 mt-1">Saved as the first entry in this trip&apos;s internal log. Hidden from drivers and riders.</p>
        </Section>

        <div className="flex justify-end items-center gap-3 pt-2 border-t border-zinc-800">
          {riderFlag && (riderFlag.severity || "").toLowerCase() === "block" && (
            <div className="text-[11px] text-red-300 font-bold uppercase tracking-wider mr-auto" data-testid="bookride-block-notice">
              🚫 Booking disabled · rider is BLOCKED
            </div>
          )}
          <Button
            onClick={onClickBook}
            disabled={busy || !calc || (riderFlag && (riderFlag.severity || "").toLowerCase() === "block")}
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-12 px-8 disabled:bg-zinc-700 disabled:text-zinc-500"
            data-testid="bookride-submit-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Send className="h-4 w-4 mr-1"/>}
            BOOK TRIP
          </Button>
        </div>
      </Card>

      <Dialog open={showCardModal} onOpenChange={setShowCardModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100" data-testid="bookride-card-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-amber-400"/>Tokenize card &amp; book</DialogTitle></DialogHeader>
          <p className="text-[11px] text-zinc-500 mb-2">Capture the rider&apos;s card via Square. The trip is created with status <span className="font-semibold text-amber-300">paid_card</span>.</p>
          <SquareCardForm
            onToken={(token, details) => submit({ payment_id: token, brand: details?.brand, last4: details?.last4 })}
            busy={busy}
            label={`CHARGE $${(calc?.total || 0).toFixed(2)}`}
          />
        </DialogContent>
      </Dialog>

      {/* v2.7.65+E112 · Fare Preset Editor modal (L5 only). Add / edit /
          delete rate profiles without a code push. Protected rows
          (weatherford + statewide) can be edited but not deleted. */}
      {showPresetEditor && (
        <FarePresetEditor
          presets={presets.all_presets || presets.presets || []}
          onClose={() => setShowPresetEditor(false)}
          onChange={reloadPresets}
        />
      )}
    </div>
  );
}

// v2.7.65+E112 · Fare Preset Editor · L5 admin CRUD for rate profiles
function FarePresetEditor({ presets, onClose, onChange }) {
  const emptyDraft = {
    id: "", label: "", description: "",
    base_fare: 0, per_mile: 0, per_minute: 0, per_passenger: 0,
    per_stop_per_minute: 0, sort_order: 100,
    starts_at: "", ends_at: "",   // v2.7.65+E114 · optional window
  };
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [cloningId, setCloningId] = useState(null);

  const setD = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));

  const startEdit = (row) => {
    setEditingId(row.id);
    setDraft({
      id: row.id, label: row.label || "",
      description: row.description || "",
      base_fare: Number(row.base_fare) || 0,
      per_mile: Number(row.per_mile) || 0,
      per_minute: Number(row.per_minute) || 0,
      per_passenger: Number(row.per_passenger) || 0,
      per_stop_per_minute: Number(row.per_stop_per_minute) || 0,
      sort_order: Number(row.sort_order) || 100,
      starts_at: (row.starts_at || "").slice(0, 10),
      ends_at:   (row.ends_at   || "").slice(0, 10),
    });
  };
  const startCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  // v2.7.65+E114 · One-tap clone via the backend clone endpoint. The
  // fresh copy pops into edit mode so the L5 admin can immediately
  // rename + tweak numbers before saving.
  const cloneRow = async (row) => {
    setCloningId(row.id);
    try {
      const r = await adminApi.post(`/admin/booked-trips/fare-presets/${row.id}/clone`);
      toast.success(`Cloned · ${r.data?.label || row.label}`);
      await onChange();
      // Move the freshly cloned row into edit mode
      if (r.data) startEdit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally { setCloningId(null); }
  };

  // v2.7.65+E114 · Guard the window: if both dates are set, starts <= ends.
  const windowError = (() => {
    if (draft.starts_at && draft.ends_at && draft.starts_at > draft.ends_at) {
      return "Start date must be on or before end date";
    }
    return null;
  })();

  const save = async () => {
    if (!draft.label || (!editingId && !draft.id)) {
      toast.error("Label + ID required");
      return;
    }
    if (windowError) {
      toast.error(windowError);
      return;
    }
    setSaving(true);
    try {
      // Normalize empty dates to null so the backend can clear them.
      const payload = {
        ...draft,
        starts_at: (draft.starts_at || "").trim() || null,
        ends_at:   (draft.ends_at   || "").trim() || null,
      };
      if (editingId) {
        const { id, ...rest } = payload;
        await adminApi.patch(`/admin/booked-trips/fare-presets/${editingId}`, rest);
        toast.success(`Preset updated · ${draft.label}`);
      } else {
        await adminApi.post("/admin/booked-trips/fare-presets", payload);
        toast.success(`Preset created · ${draft.label}`);
      }
      setDraft(emptyDraft);
      setEditingId(null);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (row.protected) return toast.error("This preset is protected — edit values instead");
    if (!window.confirm(`Delete rate profile "${row.label}"? This cannot be undone.`)) return;
    setDeletingId(row.id);
    try {
      await adminApi.delete(`/admin/booked-trips/fare-presets/${row.id}`);
      toast.success(`Deleted · ${row.label}`);
      if (editingId === row.id) { setEditingId(null); setDraft(emptyDraft); }
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setDeletingId(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
         data-testid="fare-preset-editor-modal"
         onClick={() => !saving && !deletingId && !cloningId && onClose()}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="font-bold text-lg text-amber-300 flex items-center gap-2">
            <Settings2 className="h-5 w-5"/>Rate profile editor
          </h3>
          <button type="button" onClick={onClose}
                  className="text-zinc-500 hover:text-white p-1"
                  data-testid="fare-preset-editor-close">
            <X className="h-4 w-4"/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Existing presets list */}
          <div className="md:col-span-2 space-y-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1">
              Existing presets ({presets.length})
            </div>
            {presets.map((row) => (
              <div key={row.id}
                   data-testid={`fare-preset-row-${row.id}`}
                   className={`rounded border p-2 ${editingId === row.id
                     ? "border-amber-400 bg-amber-900/10"
                     : "border-zinc-800 bg-zinc-950"}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-zinc-100 truncate flex items-center gap-1.5 flex-wrap">
                      {row.label}
                      {row.protected && <ShieldCheck className="h-3 w-3 text-emerald-400 flex-shrink-0" title="Protected · cannot delete"/>}
                      {row.is_active === false && (
                        <span
                          data-testid={`fare-preset-badge-off-${row.id}`}
                          className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700"
                          title="Outside the active window · hidden from dispatchers"
                        >
                          Off season
                        </span>
                      )}
                      {(row.starts_at || row.ends_at) && row.is_active !== false && (
                        <span
                          data-testid={`fare-preset-badge-window-${row.id}`}
                          className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 border border-emerald-800 flex items-center gap-0.5"
                          title="Effective window active"
                        >
                          <CalendarClock className="h-2.5 w-2.5"/>Windowed
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-500 truncate">{row.id}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">
                      ${row.base_fare} drop · ${row.per_mile}/mi · ${row.per_passenger}/pax
                    </div>
                    {(row.starts_at || row.ends_at) && (
                      <div className="text-[10px] text-zinc-500 mt-0.5" data-testid={`fare-preset-window-${row.id}`}>
                        {row.starts_at ? row.starts_at.slice(0, 10) : "—"} → {row.ends_at ? row.ends_at.slice(0, 10) : "—"}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      type="button" onClick={() => cloneRow(row)}
                      disabled={cloningId === row.id}
                      data-testid={`fare-preset-clone-${row.id}`}
                      className="p-1.5 rounded bg-zinc-800 hover:bg-blue-900 text-blue-300"
                      title="Clone into a new preset"
                    >
                      {cloningId === row.id
                        ? <Loader2 className="h-3 w-3 animate-spin"/>
                        : <Copy className="h-3 w-3"/>}
                    </button>
                    <button
                      type="button" onClick={() => startEdit(row)}
                      data-testid={`fare-preset-edit-${row.id}`}
                      className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-amber-300"
                      title="Edit values"
                    >
                      <Pencil className="h-3 w-3"/>
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(row)}
                      disabled={row.protected || deletingId === row.id}
                      data-testid={`fare-preset-delete-${row.id}`}
                      className={`p-1.5 rounded ${row.protected
                        ? "bg-zinc-900 text-zinc-700 cursor-not-allowed"
                        : "bg-zinc-800 hover:bg-rose-900 text-rose-300"}`}
                      title={row.protected ? "Protected · cannot delete" : "Delete preset"}
                    >
                      {deletingId === row.id
                        ? <Loader2 className="h-3 w-3 animate-spin"/>
                        : <Trash2 className="h-3 w-3"/>}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Editor form */}
          <div className="md:col-span-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">
                {editingId ? `Editing · ${editingId}` : "Add new preset"}
              </div>
              {editingId && (
                <button type="button" onClick={startCreate}
                        data-testid="fare-preset-cancel-edit"
                        className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 hover:text-white">
                  Cancel
                </button>
              )}
            </div>
            <div className="space-y-2">
              {!editingId && (
                <Field testid="fare-preset-id" label="Slug · lowercase / dashes only" value={draft.id} onChange={setD("id")} placeholder="holiday_surge"/>
              )}
              <Field testid="fare-preset-label" label="Label" value={draft.label} onChange={setD("label")} placeholder="Holiday Surge Rate"/>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Description</Label>
                <Textarea
                  rows={2}
                  value={draft.description}
                  onChange={(e) => setD("description")(e.target.value)}
                  data-testid="fare-preset-desc"
                  placeholder="e.g. Thanksgiving weekend · +15% surcharge"
                  className="bg-zinc-950 border-zinc-700 mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field testid="fare-preset-base" label="Base fare $" type="number" value={draft.base_fare} onChange={(v) => setD("base_fare")(Number(v) || 0)}/>
                <Field testid="fare-preset-mile" label="Per mile $" type="number" value={draft.per_mile} onChange={(v) => setD("per_mile")(Number(v) || 0)}/>
                <Field testid="fare-preset-min" label="Per minute $" type="number" value={draft.per_minute} onChange={(v) => setD("per_minute")(Number(v) || 0)}/>
                <Field testid="fare-preset-pax" label="Per passenger $" type="number" value={draft.per_passenger} onChange={(v) => setD("per_passenger")(Number(v) || 0)}/>
                <Field testid="fare-preset-stopmin" label="Per stop-minute $" type="number" value={draft.per_stop_per_minute} onChange={(v) => setD("per_stop_per_minute")(Number(v) || 0)}/>
                <Field testid="fare-preset-sort" label="Sort order" type="number" value={draft.sort_order} onChange={(v) => setD("sort_order")(Number(v) || 100)}/>
              </div>

              {/* v2.7.65+E114 · Effective window */}
              <div className="rounded border border-zinc-800 bg-zinc-950 p-2.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CalendarClock className="h-3.5 w-3.5 text-emerald-400"/>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">
                    Effective window (optional)
                  </span>
                </div>
                <div className="text-[10px] text-zinc-500 mb-2">
                  Leave both blank for always-on. When set, the preset only shows to dispatchers within the window and re-arms automatically on the start date.
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Starts</Label>
                    <input
                      type="date"
                      value={draft.starts_at || ""}
                      onChange={(e) => setD("starts_at")(e.target.value)}
                      data-testid="fare-preset-starts-at"
                      className="w-full bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-100 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Ends</Label>
                    <input
                      type="date"
                      value={draft.ends_at || ""}
                      onChange={(e) => setD("ends_at")(e.target.value)}
                      data-testid="fare-preset-ends-at"
                      className="w-full bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-100 mt-1"
                    />
                  </div>
                </div>
                {windowError && (
                  <div className="mt-1 text-[11px] text-rose-400" data-testid="fare-preset-window-error">
                    {windowError}
                  </div>
                )}
              </div>

              <Button
                onClick={save}
                disabled={saving || !!windowError}
                data-testid="fare-preset-save"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold w-full mt-2"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Check className="h-4 w-4 mr-1"/>}
                {editingId ? "Update preset" : "Add preset"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">{title}</div>
      {children}
    </div>
  );
}

function Field({ testid, label, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={onChange} placeholder={placeholder} className="bg-zinc-950 border-zinc-700 mt-1"/>
    </div>
  );
}

function PaymentPill({ kind, current, onClick, icon: Icon, label, testid }) {
  const active = current === kind;
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded border text-xs font-semibold transition flex items-center gap-2 ${
      active ? "bg-amber-400/15 border-amber-400 text-amber-200" : "bg-zinc-950 border-zinc-700 text-zinc-400 hover:border-zinc-600"
    }`}>
      <Icon className="h-3.5 w-3.5"/>{label}
    </button>
  );
}

// ===================== Bookings list + detail =====================
function BookingsList() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  // v2.7.65+E262 · Loyalty "10th ride free" (Weatherford) flags, keyed by trip id.
  const [freeRide, setFreeRide] = useState({});

  const reload = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/booked-trips", { params: { status: statusFilter, limit: 200 } });
      const list = data.items || [];
      setItems(list);
      // Ask the backend which of these bookings are the rider's free 10th ride.
      try {
        const payloadItems = list
          .filter((t) => (t.rider?.phone || "").trim() && !["Completed", "Cancelled", "Canceled"].includes(t.status))
          .map((t) => ({ id: t.id, phone: t.rider.phone, pickup: t.starting_address || "", dropoff: t.ending_address || "" }));
        if (payloadItems.length) {
          const r = await adminApi.post("/admin/loyalty/free-ride-check", { items: payloadItems });
          setFreeRide(r.data.results || {});
        } else {
          setFreeRide({});
        }
      } catch { /* eligibility is advisory — never block the queue */ }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load bookings"); }
  }, [statusFilter]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-3" data-testid="bookings-list">
      <Card className="bg-zinc-900 border-zinc-800 p-3 flex items-center gap-2 flex-wrap">
        <Search className="h-4 w-4 text-zinc-500"/>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-zinc-950 border-zinc-700 w-[200px]" data-testid="bookings-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
            <SelectItem value="all">All statuses</SelectItem>
            {TRIP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" onClick={reload} className="text-zinc-400" data-testid="bookings-reload"><RefreshCw className="h-3.5 w-3.5"/></Button>
        <span className="text-[10px] text-zinc-500 ml-auto">{items.length} booking{items.length === 1 ? "" : "s"}</span>
      </Card>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-xs text-zinc-500 text-center py-8">No bookings match.</div>}
        {items.map((t) => (
          <Card key={t.id} className="bg-zinc-900 border-zinc-800 p-3 flex items-start gap-3" data-testid={`booking-row-${t.id}`}>
            <MapPin className="h-5 w-5 text-amber-400 mt-1 flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-sm font-bold">{t.id}</div>
                {/* v2.7.10 · Loyalty tier inline next to trip ID so dispatch can
                    prioritize VIP riders at a glance from the queue. */}
                <TierBadge tier={t.rider_tier} label={t.rider_tier_label} tripCount={t.rider_trip_count}/>
                <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${t.status === "Completed" ? "bg-green-500/20 text-green-300" : "bg-amber-400/15 text-amber-300"}`}>{t.status}</span>
                <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${t.payment_method === "card" ? "bg-blue-500/20 text-blue-300" : "bg-zinc-700 text-zinc-300"}`}>{t.payment_method}</span>
                {t.source === "public_quick_booking" && (
                  <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/40" data-testid={`booking-public-${t.id}`}>Public · Web</span>
                )}
                {/* v2.7.65+E158 · Dispatch-created bookings get a distinct
                    cyan badge so operators can tell them apart from public
                    web bookings at a glance. */}
                {t.source === "admin_dispatch" && (
                  <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold bg-yellow-500/20 text-yellow-200 border border-yellow-400/60" data-testid={`booking-dispatch-${t.id}`}>Dispatch Booking</span>
                )}
                {/* v2.7.65+E132 · Voucher indicator so dispatch verifies the
                    physical voucher's serial at pickup. */}
                {t.is_voucher_trip && (
                  <span
                    className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-black bg-amber-500/25 text-amber-200 border border-amber-400/70"
                    title={`Voucher trip · ${t.voucher_serial_number || "(no serial)"} · verify at pickup`}
                    data-testid={`booking-voucher-${t.id}`}
                  >
                    VOUCHER · #{t.voucher_serial_number || "?"}
                  </span>
                )}
                {/* v2.7.65+E262 · Loyalty 10th-ride-free flag (Weatherford). */}
                {freeRide[t.id]?.eligible && (
                  <span
                    className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-black bg-emerald-500/25 text-emerald-200 border border-emerald-400/70 inline-flex items-center gap-1"
                    title="Rider's 10th completed ride within Weatherford city limits — comp this fare (do NOT charge)."
                    data-testid={`booking-freeride-${t.id}`}
                  >
                    <Gift className="h-3 w-3"/> FREE RIDE · LOYALTY 10TH
                  </span>
                )}
                {/* v2.8 · Employee (staff) self-booking flag. */}
                {t.staff_booking && (
                  <span
                    className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-black bg-indigo-500/25 text-indigo-200 border border-indigo-400/70 inline-flex items-center gap-1"
                    title={`Booked by a staff member${t.staff_booking_role ? ` (${t.staff_booking_role})` : ""} riding as themselves.`}
                    data-testid={`booking-employee-${t.id}`}
                  >
                    <BadgeCheck className="h-3 w-3"/> EMPLOYEE{t.staff_booking_role ? ` · ${String(t.staff_booking_role).replace(/_/g, " ").toUpperCase()}` : ""}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-zinc-400 truncate">
                {(((t.rider?.first_name || "") + " " + (t.rider?.last_name || "")).trim() || t.rider?.name || "(no name)")} · {t.rider?.phone || "—"} · ${(t.total || 0).toFixed(2)} · {new Date(t.created_at).toLocaleString()}
              </div>
              <div className="text-[10px] text-zinc-500 truncate">{t.starting_address} → {t.ending_address}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setOpenId(t.id)} className="text-amber-400" data-testid={`open-booking-${t.id}`}>Review</Button>
          </Card>
        ))}
      </div>

      <BookingDetailDialog tripId={openId} freeRide={openId ? freeRide[openId] : null} onClose={() => setOpenId(null)} onChange={reload}/>
    </div>
  );
}

function BookingDetailDialog({ tripId, freeRide, onClose, onChange }) {
  const [trip, setTrip] = useState(null);
  const [notes, setNotes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // v2.8 · Redeem-at-Pickup free-ride PIN
  const [pinDraft, setPinDraft] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const [pinDone, setPinDone] = useState(null);
  const [pinOwner, setPinOwner] = useState(null);   // {found, rider_name} once 6 digits typed

  // Preview whose free ride a typed code belongs to (read-only) so the
  // dispatcher never logs a redemption on the wrong trip.
  useEffect(() => {
    if (pinDone) return;
    if (pinDraft.length !== 6) { setPinOwner(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await adminApi.get("/admin/loyalty/pin-owner", { params: { pin: pinDraft } });
        if (!cancelled) setPinOwner(data);
      } catch { if (!cancelled) setPinOwner({ found: false }); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [pinDraft, pinDone]);

  useEffect(() => {
    if (!tripId) { setTrip(null); setNotes([]); setDraft(""); setPinDraft(""); setPinDone(null); setPinOwner(null); return; }
    setPinDraft(""); setPinDone(null); setPinOwner(null);
    adminApi.get(`/admin/booked-trips/${tripId}`).then((r) => setTrip(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Load failed"));
    adminApi.get(`/admin/booked-trips/${tripId}/notes`).then((r) => setNotes(r.data.items || [])).catch(() => {});
    adminApi.get("/admin/drivers").then((r) => setDrivers((r.data || []).filter((d) => d.active !== false))).catch(() => {});
  }, [tripId]);

  const reload = async () => {
    const [t, n] = await Promise.all([
      adminApi.get(`/admin/booked-trips/${tripId}`),
      adminApi.get(`/admin/booked-trips/${tripId}/notes`),
    ]);
    setTrip(t.data); setNotes(n.data.items || []);
    if (typeof onChange === "function") onChange();
  };

  const addNote = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try { await adminApi.post(`/admin/booked-trips/${tripId}/notes`, { body: draft.trim() }); setDraft(""); await reload(); toast.success("Note added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Note failed"); }
    finally { setBusy(false); }
  };

  const redeemFreeRidePin = async () => {
    const code = (pinDraft || "").replace(/\D/g, "");
    if (code.length !== 6) { toast.error("Enter the rider's 6-digit free-ride code."); return; }
    setPinBusy(true);
    try {
      const { data } = await adminApi.post("/admin/loyalty/verify-pin", { pin: code, trip_id: tripId });
      setPinDone({ rider_name: data.rider_name });
      setPinDraft("");
      await reload();
      toast.success(`Free ride redeemed for ${data.rider_name} · comp the fare`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid or already-redeemed code"); }
    finally { setPinBusy(false); }
  };

  const removeNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try { await adminApi.delete(`/admin/booked-trips/${tripId}/notes/${noteId}`); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const assignDriver = async (driverId) => {
    setBusy(true);
    try { await adminApi.put(`/admin/booked-trips/${tripId}/assign`, { driver_id: driverId, notify: true }); await reload(); toast.success("Driver assigned · they were notified"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try { await adminApi.put(`/admin/booked-trips/${tripId}/status`, { status }); await reload(); toast.success(`Status → ${status}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Status failed"); }
    finally { setBusy(false); }
  };

  const downloadTxt = () => fetchAndOpen(`/api/admin/booked-trips/${tripId}/notes/export.txt`, true);
  const printHtml = () => fetchAndOpen(`/api/admin/booked-trips/${tripId}/notes/print.html`);
  const downloadPdf = async () => {
    if (!notes.length) return toast.error("No notes to export");
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      doc.setFontSize(14); doc.setTextColor(182, 132, 26); doc.text(`Dispatch Notes · ${tripId}`, 14, 18);
      doc.setFontSize(9); doc.setTextColor(80);
      doc.text(`Rider: ${(trip?.rider?.first_name || "") + " " + (trip?.rider?.last_name || "")}`, 14, 26);
      doc.text(`Status: ${trip?.status || "—"} · Total $${(trip?.total || 0).toFixed(2)}`, 14, 32);
      let y = 42;
      doc.setFontSize(10);
      notes.forEach((n) => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.setTextColor(120); doc.text(`${n.created_at} · ${n.author} (${n.author_role})`, 14, y); y += 5;
        doc.setTextColor(20);
        const lines = doc.splitTextToSize(n.body || "", 180);
        lines.forEach((ln) => { if (y > 280) { doc.addPage(); y = 20; } doc.text(ln, 14, y); y += 5; });
        y += 4;
      });
      doc.save(`dispatch-notes-${tripId}.pdf`);
    } catch (e) { toast.error("PDF generation failed"); console.warn(e); }
  };

  if (!tripId) return null;

  return (
    <Dialog open={!!tripId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="booking-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <MapPin className="h-5 w-5 text-amber-400"/>{tripId}
            {/* v2.7.10 · Loyalty tier surfaced in the detail header too. */}
            {trip && <TierBadge tier={trip.rider_tier} label={trip.rider_tier_label} tripCount={trip.rider_trip_count} size="sm"/>}
          </DialogTitle>
        </DialogHeader>
        {!trip ? <div className="py-8 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2"/>Loading…</div> : (
          <div className="space-y-4 text-sm">
            {/* v2.8 · Employee (staff) self-booking notice. */}
            {trip.staff_booking && (
              <div className="rounded-lg border-2 border-indigo-400/70 bg-indigo-500/15 px-4 py-3 flex items-center gap-3" data-testid="booking-detail-employee">
                <BadgeCheck className="h-6 w-6 text-indigo-300 flex-shrink-0"/>
                <div>
                  <div className="text-sm font-black text-indigo-200 uppercase tracking-wide">Employee Ride{trip.staff_booking_role ? ` · ${String(trip.staff_booking_role).replace(/_/g, " ").toUpperCase()}` : ""}</div>
                  <div className="text-xs text-indigo-100/90">Booked by a staff member riding as themselves — handy for reporting/exclusion from rider metrics.</div>
                </div>
              </div>
            )}
            {/* v2.7.65+E262 · Loyalty free-ride notice — comp this fare. */}
            {freeRide?.eligible && (
              <div className="rounded-lg border-2 border-emerald-400/70 bg-emerald-500/15 px-4 py-3 flex items-center gap-3" data-testid="booking-detail-freeride">
                <Gift className="h-6 w-6 text-emerald-300 flex-shrink-0"/>
                <div>
                  <div className="text-sm font-black text-emerald-200 uppercase tracking-wide">Free Ride — Loyalty 10th</div>
                  <div className="text-xs text-emerald-100/90">This is the rider's 10th completed ride within Weatherford city limits. Comp the fare — do NOT charge.</div>
                </div>
              </div>
            )}

            {/* v2.8 · Redeem-at-Pickup free-ride PIN. Enter the one-time code
                the rider shows in their portal to mark the free ride redeemed
                (records it + logs a note — never zeroes the fare). */}
            <Card className="bg-zinc-950 border-amber-400/30 p-3" data-testid="freeride-redeem-dispatch">
              <div className="flex items-center gap-1.5 mb-2">
                <KeyRound className="h-3.5 w-3.5 text-amber-400"/>
                <span className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Redeem Free-Ride Code (loyalty)</span>
              </div>
              {pinDone ? (
                <div className="flex items-center gap-2 text-sm font-bold text-emerald-300" data-testid="freeride-redeem-done-dispatch">
                  <Check className="h-4 w-4"/> Redeemed for {pinDone.rider_name} · comp the fare (logged to notes)
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Input
                      value={pinDraft}
                      onChange={(e) => setPinDraft(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      inputMode="numeric"
                      placeholder="Rider's 6-digit code"
                      className="bg-zinc-900 border-zinc-700 font-mono tracking-[0.25em] text-center max-w-[220px]"
                      data-testid="freeride-pin-input-dispatch"
                    />
                    <Button
                      onClick={redeemFreeRidePin}
                      disabled={pinBusy || pinDraft.length !== 6}
                      className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-black"
                      data-testid="freeride-pin-submit-dispatch"
                    >
                      {pinBusy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Redeem"}
                    </Button>
                  </div>
                  {pinDraft.length === 6 && pinOwner && (
                    pinOwner.found ? (
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs font-bold text-emerald-300" data-testid="freeride-pin-owner-dispatch">
                        <Check className="h-3.5 w-3.5"/> Code belongs to <span className="underline underline-offset-2">{pinOwner.rider_name}</span> — confirm this is your rider before redeeming.
                      </div>
                    ) : (
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs font-bold text-rose-300" data-testid="freeride-pin-owner-none-dispatch">
                        <X className="h-3.5 w-3.5"/> No active free ride matches this code.
                      </div>
                    )
                  )}
                </>
              )}
              <div className="text-[10px] text-zinc-500 mt-1.5">One-time use · verified by driver or dispatch · does not zero the fare.</div>
            </Card>

            <Card className="bg-zinc-950 border-zinc-800 p-3 grid grid-cols-2 gap-2">
              <KV k="Rider" v={(`${trip.rider?.first_name || ""} ${trip.rider?.last_name || ""}`).trim() || trip.rider?.name || "—"}/>
              <KV k="Phone" v={trip.rider?.phone || "—"}/>
              <KV k="Email" v={trip.rider?.email || "—"}/>
              <KV k="Address" v={trip.rider?.address || "—"}/>
              <KV k="From" v={trip.starting_address}/>
              <KV k="To" v={trip.ending_address}/>
              <KV k="Distance" v={`${trip.miles || "—"} mi · ${trip.minutes || "—"} min`}/>
              <KV k="Total" v={`$${(trip.total || 0).toFixed(2)}`}/>
              <KV k="Payment" v={`${trip.payment_method} · ${trip.payment_status}`}/>
              <KV k="Created" v={new Date(trip.created_at).toLocaleString()}/>
              {/* v2.7.7 · Gender-Preferred Dispatch — surface the rider's
                  preferred driver gender so dispatch can match accordingly. */}
              {trip.preferred_driver_gender && trip.preferred_driver_gender !== "any" && (
                <div className="col-span-2 text-xs flex items-center gap-2" data-testid="booking-preferred-gender">
                  <span className="text-zinc-500">Preferred Driver Gender:</span>
                  <Badge variant="outline" className="border-fuchsia-400/60 text-fuchsia-300 uppercase font-bold text-[10px]">
                    <ShieldCheck className="h-3 w-3 mr-1"/>{trip.preferred_driver_gender.toUpperCase()} ONLY (rider request)
                  </Badge>
                </div>
              )}
            </Card>

            <Card className="bg-zinc-950 border-amber-400/30 p-3" data-testid="booking-controls">
              <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-2">Dispatch controls</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px]">Assigned driver</Label>
                  <Select value={trip.assigned_driver_id || ""} onValueChange={assignDriver}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 mt-1" data-testid="booking-driver-select">
                      <SelectValue placeholder={trip.assigned_driver?.name || "Pick a driver…"}/>
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-72">
                      {drivers.map((d) => {
                        const pref = (trip.preferred_driver_gender || "any").toLowerCase();
                        const dg = (d.gender || "unspecified").toLowerCase();
                        const isMatch = pref === "any" || pref === dg;
                        return (
                          <SelectItem key={d.id} value={d.id} data-testid={`booking-driver-opt-${d.id}`}>
                            <span className="flex items-center gap-1.5">
                              {/* v2.7.7 · Gender badge — green if matches rider preference, gray otherwise */}
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isMatch ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>
                                {dg === "male" ? "M" : dg === "female" ? "F" : "U"}
                              </span>
                              <span>{d.name} {d.plate_number ? `· ${d.plate_number}` : ""}</span>
                              {!isMatch && pref !== "any" && <span className="text-[9px] text-amber-400">(mismatch)</span>}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {trip.preferred_driver_gender && trip.preferred_driver_gender !== "any" && (
                    <div className="text-[10px] text-fuchsia-300/90 mt-1" data-testid="booking-gender-filter-hint">
                      Rider preferred {trip.preferred_driver_gender} drivers. Green badge = match.
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px]">Status</Label>
                  <Select value={trip.status} onValueChange={setStatus}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 mt-1" data-testid="booking-status-select"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                      {TRIP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* v2.7.0 batch DG · prominent one-tap completion buttons. Both
                  call the same `/admin/booked-trips/{id}/status` endpoint
                  that now mirrors a Completed booking into the Driver Trips
                  log with full financial info. */}
              {trip.status !== "Completed" && trip.status !== "Cancelled" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 pt-3 border-t border-zinc-800">
                  <Button
                    data-testid="booking-mark-complete"
                    onClick={() => setStatus("Completed")}
                    disabled={busy}
                    className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-extrabold uppercase tracking-wider"
                  >
                    {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Check className="h-4 w-4 mr-1"/>}
                    Mark Complete · ${(trip.total || 0).toFixed(2)}
                  </Button>
                  <Button
                    data-testid="booking-mark-cancelled"
                    onClick={() => { if (window.confirm("Cancel this trip?")) setStatus("Cancelled"); }}
                    disabled={busy}
                    variant="outline"
                    className="border-red-700/50 text-red-300 hover:bg-red-900/30 font-bold uppercase tracking-wider"
                  >
                    <X className="h-4 w-4 mr-1"/>Cancel Trip
                  </Button>
                </div>
              )}
              {trip.status === "Completed" && (
                <div className="mt-3 pt-3 border-t border-emerald-700/30 text-xs text-emerald-300 flex items-center gap-1.5" data-testid="booking-completed-banner">
                  <Check className="h-4 w-4"/>
                  Trip completed · mirrored to Driver Trips log
                  {trip.completed_at ? ` on ${new Date(trip.completed_at).toLocaleString()}` : ""}
                  {trip.completed_by ? ` by ${trip.completed_by}` : ""}.
                </div>
              )}
            </Card>

            <Card className="bg-zinc-950 border-zinc-800 p-3" data-testid="booking-notes-card">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Internal log · {notes.length} entries (admin/dispatch only)</div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={downloadTxt} className="text-zinc-300 text-[10px]" data-testid="notes-download-txt"><Download className="h-3.5 w-3.5 mr-1"/>TXT</Button>
                  <Button size="sm" variant="ghost" onClick={downloadPdf} className="text-zinc-300 text-[10px]" data-testid="notes-download-pdf"><FileText className="h-3.5 w-3.5 mr-1"/>PDF</Button>
                  <Button size="sm" variant="ghost" onClick={printHtml} className="text-zinc-300 text-[10px]" data-testid="notes-print"><Printer className="h-3.5 w-3.5 mr-1"/>Print</Button>
                </div>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {notes.map((n) => (
                  <div key={n.id} className="border border-zinc-800 rounded p-2" data-testid={`booking-note-${n.id}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-zinc-500">{new Date(n.created_at).toLocaleString()} · {n.author} <span className="text-zinc-600">({n.author_role})</span>{n.context ? ` · ${n.context}` : ""}</div>
                        <div className="text-xs text-zinc-200 whitespace-pre-wrap mt-0.5">{n.body}</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeNote(n.id)} className="text-red-400 h-6"><Trash2 className="h-3 w-3"/></Button>
                    </div>
                  </div>
                ))}
                {notes.length === 0 && <div className="text-[11px] text-zinc-600 italic">No notes yet.</div>}
              </div>
              <div className="mt-3 flex gap-2">
                <Textarea data-testid="booking-note-draft" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add a note… (e.g., 'Driver running 5 min late, called rider to let them know.')" className="bg-zinc-900 border-zinc-700 min-h-[60px]"/>
                <Button onClick={addNote} disabled={busy || !draft.trim()} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="booking-note-add">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Add"}</Button>
              </div>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KV({ k, v }) {
  return <div className="text-xs"><span className="text-zinc-500">{k}: </span><span className="text-zinc-200 font-medium">{v}</span></div>;
}

// v2.7.65+E111 · Live fare-component breakdown row
function BreakdownRow({ label, amount, note, testid }) {
  const val = Number(amount) || 0;
  return (
    <div className="flex justify-between items-baseline gap-2" data-testid={testid}>
      <span className="text-zinc-400 truncate">
        {label}
        {note ? <span className="text-[10px] text-zinc-600 ml-1">({note})</span> : null}
      </span>
      <span className={`tabular-nums font-bold ${val > 0 ? "text-amber-300" : "text-zinc-600"}`}>
        ${val.toFixed(2)}
      </span>
    </div>
  );
}

async function fetchAndOpen(path, asDownload = false) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("rtg_admin_token");
  try {
    const r = await fetch(base + path, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(r.statusText);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    if (asDownload) {
      const a = document.createElement("a");
      a.href = url;
      // Pull filename from header or fall back to a sane default
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "dispatch-notes.txt";
      a.click();
    } else {
      window.open(url, "_blank");
    }
  } catch (e) { toast.error(`Action failed: ${e.message || e}`); }
}

// ===================== Bottleneck Ping =====================
function BottleneckPingPanel() {
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("all_active");
  const [drivers, setDrivers] = useState([]);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/drivers").then((r) => setDrivers((r.data || []).filter((d) => d.active !== false))).catch(() => {});
  }, []);

  const sendPing = async () => {
    if (!body.trim()) return toast.error("Ping message is required");
    const driver_ids = target === "specific" ? Object.keys(picked).filter((id) => picked[id]) : null;
    if (target === "specific" && (!driver_ids || driver_ids.length === 0)) return toast.error("Select at least one driver");
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/dispatch/bottleneck-ping", { body: body.trim(), target, driver_ids });
      toast.success(`Ping sent to ${data.sent_to} driver${data.sent_to === 1 ? "" : "s"}`);
      setBody(""); setPicked({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Ping failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="bg-zinc-900 border-red-500/40 p-5 space-y-4" data-testid="bottleneck-panel">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 text-red-400 mt-1"/>
        <div>
          <h3 className="font-bold text-red-300">Driver Bottleneck Ping</h3>
          <p className="text-[11px] text-zinc-400 leading-snug">Sends an urgent in-app notification to drivers — even if they&apos;re already on a trip — so dispatch can surface a sudden bottleneck (high demand, lost driver, route closure, etc.). Drivers see the message at the top of their dashboard inbox.</p>
        </div>
      </div>
      <Textarea
        data-testid="ping-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Example: 'Heavy demand at OU stadium tonight — head that direction if you're available.'"
        className="bg-zinc-950 border-zinc-700 min-h-[100px]"
      />
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs text-zinc-400">Target:</Label>
        <PingTargetPill kind="all_active" current={target} onClick={() => setTarget("all_active")} label="All active drivers" testid="ping-target-all"/>
        <PingTargetPill kind="specific" current={target} onClick={() => setTarget("specific")} label="Specific driver(s)" testid="ping-target-specific"/>
      </div>
      {target === "specific" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto border border-zinc-800 rounded p-2" data-testid="ping-driver-list">
          {drivers.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-xs text-zinc-300 hover:bg-zinc-950/40 rounded px-2 py-1 cursor-pointer">
              <input type="checkbox" checked={!!picked[d.id]} onChange={(e) => setPicked((p) => ({ ...p, [d.id]: e.target.checked }))} data-testid={`ping-pick-${d.id}`}/>
              {d.name}
            </label>
          ))}
        </div>
      )}
      <Button onClick={sendPing} disabled={busy || !body.trim()} className="w-full h-12 bg-red-500 hover:bg-red-400 text-white font-bold" data-testid="ping-send-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <Send className="h-4 w-4 mr-1"/>}SEND URGENT PING
      </Button>
    </Card>
  );
}

function PingTargetPill({ kind, current, onClick, label, testid }) {
  const active = current === kind;
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={`px-3 py-1.5 rounded border text-[11px] font-semibold ${
      active ? "bg-red-500/20 border-red-400 text-red-200" : "bg-zinc-950 border-zinc-700 text-zinc-400 hover:border-zinc-600"
    }`}>{label}</button>
  );
}
